import { RouteObject } from "react-router-dom";
import { AddBidPackagePage } from "src/routes/bids/add-bid-package/AddBidPackagePage";
import { BidPackageDetailPage } from "src/routes/bids/BidPackageDetailPage";
import { BidPackagesPage } from "src/routes/bids/BidPackagesPage";
import { EnterUnitBasedBidPage } from "src/routes/bids/EnterUnitBasedBidPage";
import { EnterPlanBasedBidPage } from "src/routes/bids/plan-based-bid-package/EnterPlanBasedBidPage";
import { PlanBasedBidPackagePage } from "src/routes/bids/plan-based-bid-package/PlanBasedBidPackagePage";
import { NotFound } from "src/routes/NotFound";
import { AppShell } from "src/routing/components/AppShell";
import { BeamNavbarLayout } from "src/routing/components/layouts/BeamNavbarLayout";
import { FocusedLayout } from "src/routing/components/layouts/FocusedLayout";
import { GlobalNavLayout } from "src/routing/components/layouts/GlobalNavLayout";
import { ScrollableParentLayout } from "src/routing/components/layouts/ScrollableParentLayout";
import * as bidPackagesPaths from "src/routing/domains/bidPackagesPaths";

/**
 * `/trades/bid-packages/*` route tree.
 *
 * Role: data router domain route.
 *
 * The index uses `AppShell` → `BeamNavbarLayout` because it owns Beam page layouts.
 * Bid creation, detail, and entry flows retain the legacy `GlobalNavLayout` stack.
 *
 * The plan-based bid package add/edit pages use Beam's `StepperLayout`, which per its own contract
 * must nest directly under `EnvironmentBannerLayout` — never under `GlobalNavLayout`/`NavbarLayout` —
 * so they branch off as a sibling custom-chrome stack: `AppShell` → `FocusedLayout` → page.
 *
 * Path constants live in `bidPackagesPaths.ts` (imported by `RouteUrls.ts` / pages) to avoid a cycle
 * through this file's page `Component` imports. Other `/trades/*` pages live in `trades.tsx`.
 */
const bidPackagesDomainRoute: RouteObject = {
  path: bidPackagesPaths.bidPackagesRoute.path,
  Component: AppShell,
  children: [
    { Component: BeamNavbarLayout, children: [{ index: true, Component: BidPackagesPage }] },
    {
      Component: GlobalNavLayout,
      children: [
        {
          Component: ScrollableParentLayout,
          children: [
            { path: bidPackagesPaths.bidPackageCreateRoute.path, Component: AddBidPackagePage },
            { path: bidPackagesPaths.bidPackageEditRoute.path, Component: AddBidPackagePage },
            { path: bidPackagesPaths.bidPackageDetailsRoute.path, Component: BidPackageDetailPage },
            { path: bidPackagesPaths.enterPlanBasedBidRoute.path, Component: EnterPlanBasedBidPage },
            { path: bidPackagesPaths.enterUnitBasedBidRoute.path, Component: EnterUnitBasedBidPage },
          ],
        },
      ],
    },
    {
      Component: FocusedLayout,
      children: [
        { path: bidPackagesPaths.planBasedBidPackageCreateRoute.path, Component: PlanBasedBidPackagePage },
        { path: bidPackagesPaths.planBasedBidPackageEditRoute.path, Component: PlanBasedBidPackagePage },
      ],
    },
    { path: "*", Component: NotFound },
  ],
};

/**
 * Migrated `/trades/bid-packages/*` route trees registered in `Router.tsx`.
 */
export const bidPackagesRoutes = [bidPackagesDomainRoute];
